import React from 'react';
import { X, Lightbulb, FileUp } from 'lucide-react';
import { Explanation, StorySlide, Insight } from '../types';

export const Modal: React.FC<{ isOpen: boolean, onClose: () => void, title: string, children: React.ReactNode, maxWidth?: string }> = ({ isOpen, onClose, title, children, maxWidth = 'max-w-2xl' }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`bg-obsidian-900 rounded-2xl border border-gold-500/20 shadow-2xl w-full ${maxWidth} max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200`}>
                <div className="flex items-center justify-between p-6 border-b border-gold-500/10">
                    <h3 className="text-xl font-bold text-gray-100">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <div className="p-6 overflow-y-auto custom-scrollbar">
                    {children}
                </div>
            </div>
        </div>
    );
};

export const ExplanationModal: React.FC<{ explanation: Explanation | null, onClose: () => void }> = ({ explanation, onClose }) => (
    <Modal isOpen={!!explanation} onClose={onClose} title="Insight Explanation">
        {explanation && (
            <div className="space-y-6">
                <div>
                    <h4 className="text-sm font-bold text-gold-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" /> What does this mean?
                    </h4>
                    <p className="text-gray-300 leading-relaxed">{explanation.meaning}</p>
                </div>
                <div>
                    <h4 className="text-sm font-bold text-gold-500 uppercase tracking-widest mb-2">Why is it important?</h4>
                    <p className="text-gray-300 leading-relaxed">{explanation.importance}</p>
                </div>
                <div className="bg-gold-900/10 border border-gold-500/20 rounded-lg p-4">
                    <h4 className="text-sm font-bold text-gold-400 uppercase tracking-widest mb-2">Recommended Action</h4>
                    <p className="text-gold-100 leading-relaxed">{explanation.nextSteps}</p>
                </div>
            </div>
        )}
    </Modal>
);

export const StorySlideModal: React.FC<{ slide: StorySlide | null, onClose: () => void }> = ({ slide, onClose }) => (
    <Modal isOpen={!!slide} onClose={onClose} title="Data Story Draft" maxWidth="max-w-4xl">
        {slide && (
            <div className="space-y-8">
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-300 to-gold-600 mb-4">{slide.title}</h2>
                    <p className="text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">{slide.narrative}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {slide.keyNumbers.map((num, i) => (
                        <div key={i} className="bg-obsidian-800 p-6 rounded-xl border border-gold-500/10 text-center">
                            <div className="text-3xl font-bold text-white mb-2">{num}</div>
                            <div className="text-xs text-gray-500 uppercase tracking-widest">Key Metric</div>
                        </div>
                    ))}
                </div>

                <div className="bg-obsidian-800 rounded-xl border border-gold-500/10 p-6 flex items-start gap-4">
                    <div className="p-3 bg-gold-900/20 rounded-lg">
                        <FileUp className="w-6 h-6 text-gold-500" />
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-200 mb-1">Visualization Suggestion</h4>
                        <p className="text-gray-400 text-sm">{slide.visualizationSuggestion}</p>
                    </div>
                </div>

                {slide.callToAction && (
                    <div className="text-center pt-4 border-t border-gold-500/10">
                        <p className="text-gold-400 font-medium">Recommendation: {slide.callToAction}</p>
                    </div>
                )}
            </div>
        )}
    </Modal>
);
